import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, LayoutList } from "lucide-react";
import { toast } from "sonner";

export default function AssetStructure({ assetId }) {
  const qc = useQueryClient();
  const { data: units = [] } = useQuery({ queryKey: ["units", assetId], queryFn: () => api.get(`/units?asset_id=${assetId}`).then((r) => r.data) });
  const { data: tenants = [] } = useQuery({ queryKey: ["users", "tenant"], queryFn: () => api.get("/users?role=tenant").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ building: "", floor: "", zone: "", name: "", surface: "", tenant_id: "" });

  const create = useMutation({
    mutationFn: (b) => api.post("/units", { ...b, asset_id: assetId, surface: b.surface ? Number(b.surface) : null, tenant_id: b.tenant_id || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["units", assetId] }); toast.success("Lot créé"); setOpen(false); setForm({ building: "", floor: "", zone: "", name: "", surface: "", tenant_id: "" }); },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/units/${id}`, patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["units", assetId] }); toast.success("Lot mis à jour"); },
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/units/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["units", assetId] }); toast.success("Lot supprimé"); },
  });

  const tenantMap = Object.fromEntries(tenants.map((t) => [t.id, t]));
  // Group units by building > floor
  const byBuilding = {};
  units.forEach((u) => {
    const b = u.building || "—"; const f = u.floor || "—";
    byBuilding[b] = byBuilding[b] || {};
    byBuilding[b][f] = byBuilding[b][f] || [];
    byBuilding[b][f].push(u);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">Structure : {Object.keys(byBuilding).length} bâtiment(s) • {units.length} lot(s)</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-unit-btn" size="sm" className="text-white" style={{ background: "#023E73" }}><Plus size={14} className="mr-1.5" /> Nouveau lot</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Créer un lot / cellule</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Bâtiment</Label><Input data-testid="unit-building" value={form.building} onChange={(e) => setForm({ ...form, building: e.target.value })} placeholder="A" /></div>
                <div><Label>Étage</Label><Input data-testid="unit-floor" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} placeholder="R+1" /></div>
                <div><Label>Zone</Label><Input data-testid="unit-zone" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="Nord" /></div>
              </div>
              <div><Label>Nom du lot</Label><Input data-testid="unit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Lot 3A" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Surface (m²)</Label><Input type="number" data-testid="unit-surface" value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })} /></div>
                <div><Label>Locataire</Label>
                  <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                    <SelectTrigger data-testid="unit-tenant"><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="unit-submit" onClick={() => create.mutate({ ...form, tenant_id: form.tenant_id === "none" ? null : form.tenant_id })} disabled={!form.name} className="text-white" style={{ background: "#023E73" }}>Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {Object.entries(byBuilding).map(([building, floors]) => (
          <Card key={building} className="p-4 border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <LayoutList size={16} className="text-slate-500" />
              <div className="font-display font-semibold">Bâtiment {building}</div>
            </div>
            {Object.entries(floors).map(([floor, list]) => (
              <div key={floor} className="mb-3 last:mb-0">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Étage {floor}</div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {list.map((u) => (
                    <div key={u.id} data-testid={`unit-${u.id}`} className="border border-slate-200 rounded-lg p-3 flex items-start justify-between group hover:border-slate-400 transition">
                      <div>
                        <div className="font-semibold text-sm">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.zone ? `Zone ${u.zone} • ` : ""}{u.surface ? `${u.surface} m²` : ""}</div>
                        {u.tenant_id && <div className="text-[11px] text-slate-700 mt-1">Locataire : {tenantMap[u.tenant_id]?.first_name} {tenantMap[u.tenant_id]?.last_name}</div>}
                      </div>
                      <button data-testid={`delete-unit-${u.id}`} onClick={() => remove.mutate(u.id)} className="p-1 rounded text-red-600 opacity-0 group-hover:opacity-100 hover:bg-red-50 transition"><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Card>
        ))}
        {units.length === 0 && <div className="text-sm text-slate-400 text-center py-8">Aucun lot enregistré pour cet actif</div>}
      </div>
    </div>
  );
}
